import React, { useState } from "react";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { useNavigate } from "react-router-dom";
import { 
    DocumentText24Filled, 
    Checkmark24Filled, 
    Print24Filled, 
    Delete24Filled,
    QrCode24Filled
 } from '@fluentui/react-icons';
import { ROUTES } from "#root/routes/constants";
import { Responder, useBatchDeleteResponders } from "#root/queries/responders";
import { RESPONDER_STATUS } from "#root/utils/responders";
import RespondersList from "#root/components/RespondersList";
import { Button } from "@fluentui/react-components";
import { useBadgePrinting } from "#root/hooks/useBadgePrinting";
import { DialogBox } from "#root/components/DialogBox";
import { showToast } from "#root/components/GlobalToaster";
import { Field } from "@fluentui/react-components";
import { USR_TEAMS } from "#root/utils/responders";
import { useTeamBadgePrinting } from "#root/hooks/useTeamBadgePrinting";
import './styles.scss';

const ApprovedResponders: React.FC = () => {
    const navigate = useNavigate();
    const { printBadges, isPrinting, printingIds } = useBadgePrinting();
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [respondersToDeleteIds, setRespondersToDeleteIds] = useState<string[]>([]);
    const [clearSelectionTrigger, setClearSelectionTrigger] = useState(0);

    const [isTeamBadgeDialogOpen, setIsTeamBadgeDialogOpen] = useState(false);  
    const [selectedUsarTeam, setSelectedUsarTeam] = useState<number | undefined>(USR_TEAMS[0]?.value);  
    const [incidentId, setIncidentId] = useState<string | undefined>();

    const batchDeleteResponders = useBatchDeleteResponders();

    const handleCheckIn = (responder: Responder) => {
        navigate(`${ROUTES.MERRGE211.CHECK_IN_RESPONDER}?responderId=${responder.id}`);
    };

    const handlePrintBadge = (responder: Responder) => printBadges([responder.id]);
    const handlePrintSelectedBadges = (selectedResponderIds: string[]) => printBadges(selectedResponderIds);

    const handleDeleteSelected = (selectedResponderIds: string[]) => {
        setRespondersToDeleteIds(selectedResponderIds);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await batchDeleteResponders.mutateAsync({ ids: respondersToDeleteIds });
            setIsDeleteDialogOpen(false);
            showToast(`Responders deleted successfully`, 'success');
            setRespondersToDeleteIds([]);
            setClearSelectionTrigger(prev => prev + 1);
        } catch (error) {
            showToast(`Failed to delete responders`, 'error');
        }
    };

    const isLoading = batchDeleteResponders.isPending || isPrinting;
  
  const handleTeamPrint = () => setIsTeamBadgeDialogOpen(true);  
  
    const {  
        printTeamBadge,  
        isPrinting: isTeamBadgePrinting,  
    } = useTeamBadgePrinting();  
      
const handleConfirmTeamPrint = async () => {
    if (!selectedUsarTeam) return;
    if (!incidentId) {
        showToast("Please select an incident before printing the team badge", "error");
        return;
    }
    try {
        await printTeamBadge(selectedUsarTeam, incidentId);
        setIsTeamBadgeDialogOpen(false);
        showToast("Team badge PDF downloaded", "success");
    } catch (e) {
        showToast("Failed to generate Team Badge PDF", "error");
    }
};

 

    const renderActions = (responder: Responder, onActionComplete?: () => void) => {
        const isPrintingThis = printingIds.includes(responder.id);

        return (
            <>
                <Button
                    icon={isPrintingThis ? <Spinner size={SpinnerSize.small} /> : <DocumentText24Filled />}
                    appearance="primary"
                    disabled={isPrinting}
                    onClick={() => {
                        handlePrintBadge(responder);
                        if (onActionComplete) onActionComplete();
                    }}
                >
                    {isPrintingThis ? "Printing..." : "Print Badge"}
                </Button>
                <Button
                    icon={<Checkmark24Filled />}
                    appearance="primary"
                    className="button-green"
                    onClick={() => {
                        handleCheckIn(responder);
                        if (onActionComplete) onActionComplete();
                    }}
                >
                    Check In
                </Button>
            </>
        );
    };

    const renderFooterActions = (selectedResponderIds: string[], onActionComplete?: () => void) => {
        return (
            <>
                <Button
                    icon={<Delete24Filled />}
                    appearance="primary"
                    className="button-red"
                    disabled={isLoading || selectedResponderIds.length === 0}
                    onClick={() => {
                        handleDeleteSelected(selectedResponderIds);
                        if (onActionComplete) onActionComplete();
                    }}
                >
                    Delete Selected
                </Button>
                <Button
                    appearance="primary"
                    icon={isPrinting ? <Spinner size={SpinnerSize.small} /> : <Print24Filled />}
                    disabled={isLoading || selectedResponderIds.length === 0}
                    onClick={() => {
                        handlePrintSelectedBadges(selectedResponderIds);
                        if (onActionComplete) onActionComplete();
                    }}
                >
                    {isPrinting ? "Printing..." : "Print Selected Badges"}
                </Button>
            </>

        );
    };

    return (
        <>
            <RespondersList
                key={`approved-responders-${clearSelectionTrigger}`}
                title="Approved Responders"
                status={RESPONDER_STATUS.APPROVED}
                className="approved-responders"
                renderActions={renderActions}
                renderFooterActions={renderFooterActions}
                onIncidentChange={setIncidentId}
                titleButton={  
                  <Button appearance="primary"  
                          icon={<QrCode24Filled />}  
                          onClick={handleTeamPrint}
                          disabled={!incidentId}>  
                      Print Team Badge
                  </Button>  
                } 
            />

            <DialogBox
                open={isDeleteDialogOpen}
                onOpenChange={setIsDeleteDialogOpen}
                title={`Delete Responders`}
                primaryButtonText="Delete"
                onPrimaryButtonClick={handleConfirmDelete}
                isPrimaryButtonLoading={batchDeleteResponders.isPending}
            >
                Permanently delete selected responders?
                You can't undo this action.
            </DialogBox>
            <DialogBox  
                open={isTeamBadgeDialogOpen}  
                onOpenChange={setIsTeamBadgeDialogOpen}  
                title="Print Team Badge"  
                primaryButtonText="Print"  
                primaryButtonAppearance="primary"  
                onPrimaryButtonClick={handleConfirmTeamPrint}  
                isPrimaryButtonDisabled={!selectedUsarTeam || isTeamBadgePrinting}  
                isPrimaryButtonLoading={isTeamBadgePrinting}  
                secondaryButtonText="Cancel"  
                showSecondaryButton  
                onSecondaryButtonClick={() => setIsTeamBadgeDialogOpen(false)}  
                hideCloseButton={isTeamBadgePrinting}  
            >  
                <form>  
                    <Field label="US&R Team" required>  
                        <select  
                            className="usar-team-select"  
                            value={selectedUsarTeam ?? ""}  
                            onChange={e => setSelectedUsarTeam(Number(e.target.value))}  
                            required  
                        >  
                            {USR_TEAMS.map(team => (  
                                <option key={team.value} value={team.value}>  
                                    {team.label}  
                                </option>  
                            ))}  
                        </select>  
                    </Field>  
                </form>  
            </DialogBox>  
        </>
    );
};

export default ApprovedResponders;
